import { INDUSTRIES } from "../data/categoryData";

/**
 * Mirrors `utilsServices` mapping so URL industry segments line up with `/categories` rows.
 */
export const BACKEND_CATEGORY_TO_INDUSTRY_ID = {
  realestatemortgages: "real-estate",
  legal: "legal",
  nutritionmealplansrecipes: "nutrition",
  health: "health",
  jobscareer: "careers",
  bettinggaming: "betting-gaming",
};

function normalizeKey(value = "") {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export function pickMenuSlug(entry) {
  if (!entry) return "";
  if (entry.slug) return String(entry.slug);
  if (typeof entry.path === "string") {
    return entry.path.split("/").filter(Boolean).pop() || "";
  }
  return "";
}

function industryIdFromSignupCategory(category, index = 0) {
  const legacyIndustryById = Object.fromEntries(
    INDUSTRIES.map((item) => [item.id, item]),
  );
  const rawLabel = category?.name || `Category ${index + 1}`;
  const normalizedCategoryKey = normalizeKey(rawLabel);
  const mappedIndustryId = BACKEND_CATEGORY_TO_INDUSTRY_ID[normalizedCategoryKey];
  const legacyIndustry = mappedIndustryId
    ? legacyIndustryById[mappedIndustryId]
    : null;
  const slug =
    category?.slug != null && String(category.slug).trim()
      ? String(category.slug).replace(/^\//, "")
      : "";
  return (
    legacyIndustry?.id ||
    slug ||
    slugify(rawLabel) ||
    `category-${index + 1}`
  );
}

/**
 * Maps the guest subcategory page (industry segment + menu row) to backend category / subcategory ids
 * from the same `/categories` payload used at sign-up so we can compare with `user.interests`.
 *
 * @returns {{ categoryId: number|string, subCategoryId: number|string, categoryName: string, subCategoryName: string } | null}
 */
export function resolveTopicBackendIds(signupCategories, industryId, menuItem) {
  if (
    !industryId ||
    !menuItem ||
    !Array.isArray(signupCategories) ||
    !signupCategories.length
  ) {
    return null;
  }

  const topicSlug = pickMenuSlug(menuItem).trim();
  const topicLabel = String(menuItem.label || menuItem.name || "").trim();
  if (!topicSlug && !topicLabel) return null;

  let catRow = null;
  signupCategories.forEach((c, idx) => {
    if (industryIdFromSignupCategory(c, idx) === industryId) {
      catRow = c;
    }
  });
  if (!catRow || !Array.isArray(catRow.sub_categories)) return null;

  const subs = catRow.sub_categories;
  let subRow =
    subs.find((s) => {
      const sSlug = (s.slug ? String(s.slug) : slugify(s.name || "")).trim();
      return topicSlug && (sSlug === topicSlug || sSlug === decodeURIComponent(topicSlug));
    }) || null;

  if (!subRow && topicLabel) {
    subRow =
      subs.find((s) => normalizeKey(s.name) === normalizeKey(topicLabel)) || null;
  }

  if (!subRow && topicSlug && subs.length) {
    const t = normalizeKey(topicSlug.replace(/-/g, ""));
    if (t.length >= 6) {
      subRow =
        subs.find((s) => {
          const cand = normalizeKey(slugify(s.name || ""));
          return cand && cand === t;
        }) || null;
    }
  }

  if (!subRow || catRow.id == null || subRow.id == null) return null;

  return {
    categoryId: catRow.id,
    subCategoryId: subRow.id,
    categoryName: String(catRow.name || "").trim(),
    subCategoryName: String(subRow.name || "").trim(),
  };
}

export function userHasInterestForTopic(user, topicIds) {
  if (!topicIds || !user?.interests?.length) return false;
  const c = topicIds.categoryId;
  const s = topicIds.subCategoryId;
  return user.interests.some((row) => {
    const rc = row?.category_id;
    const rs = row?.sub_category_id;
    return String(rc) === String(c) && String(rs) === String(s);
  });
}
